import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { StepperPanelStep } from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/stepper-panel-step';
import type {
  BenchmarkInsightCompetitorsDesiredCompetitor,
  BenchmarkInsightCompetitorsProposedCompset,
} from 'lumen/models';
import {
  isPrioritizedCategory,
  PRIORITIZED_CATEGORIES,
  type PrioritizedCategory,
} from 'lumen/models/benchmark-insight/competitors/proposed-compset';
import type HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsRoute from 'lumen/routes/hotel/settings/competitors/benchmark/compset/builder/proposals';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsController extends Controller {
  declare model: ModelFrom<HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsRoute>;

  @service('benchmark-insight/competitors/compset')
  declare benchmarkInsightCompetitorsCompset: Services['benchmark-insight/competitors/compset'];
  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightCompetitorsDesiredCompset: Services['benchmark-insight/competitors/desired-compset'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  AMOUNT_OF_PROPOSED_COMPSETS = 3;
  PROPOSED_COMPSETS_PRIORITY: PrioritizedCategory[] = [
    PRIORITIZED_CATEGORIES.RECOMMENDED,
    PRIORITIZED_CATEGORIES.HOTEL_CLASS,
    PRIORITIZED_CATEGORIES.ADR,
    PRIORITIZED_CATEGORIES.DISTANCE,
  ];

  get isNextDisabled(): boolean {
    return !this.benchmarkInsightCompetitorsCompset
      .selectedProposedCompsetPrioritizedCategory;
  }

  get radioGroupValue(): PrioritizedCategory | undefined {
    return (
      this.benchmarkInsightCompetitorsCompset
        .selectedProposedCompsetPrioritizedCategory ?? undefined
    );
  }

  get stepperPanelDescription(): string | undefined {
    if (!this.unusedDesiredCompetitors.length) {
      return undefined;
    }

    return this.intl.t(
      'settings.competitors.benchmark.compset.builder.stepper.proposals.description',
      {
        htmlSafe: true,
        class: 'underline',
        link: 'https://help.mylighthouse.com/en/articles/6796449-competitor-rules-in-benchmark-insight',
      },
    );
  }

  get stepperPanelSteps(): StepperPanelStep[] {
    return this.visibleProposedCompsets.map((proposedCompset, index) => {
      const compsetNumber = index + 1;

      const title = this.intl.t(
        'settings.competitors.benchmark.compset.builder.proposals.compset_name',
        { compsetNumber, compsetName: proposedCompset.name },
      );

      return {
        title,
        icon: proposedCompset.icon,
        description: proposedCompset.description,
        isSelected:
          this.benchmarkInsightCompetitorsCompset
            .selectedProposedCompsetPrioritizedCategory ===
          proposedCompset.prioritizedCategory,
      };
    });
  }

  get unusedDesiredCompetitors(): BenchmarkInsightCompetitorsDesiredCompetitor[] {
    return this.benchmarkInsightCompetitorsDesiredCompset.getUnusedDesiredCompetitors(
      // @ts-expect-error Class inheritance
      this.visibleProposedCompsets,
    );
  }

  get visibleProposedCompsets(): BenchmarkInsightCompetitorsProposedCompset[] {
    const { proposedCompsets } = this.benchmarkInsightCompetitorsCompset;

    // Sort based on priority
    proposedCompsets.sort((a, b) => {
      const aPriority = this.PROPOSED_COMPSETS_PRIORITY.indexOf(
        a.prioritizedCategory,
      );
      const bPriority = this.PROPOSED_COMPSETS_PRIORITY.indexOf(
        b.prioritizedCategory,
      );

      return aPriority - bPriority;
    });

    // Only show a limited number of proposed compsets
    if (proposedCompsets.length > this.AMOUNT_OF_PROPOSED_COMPSETS) {
      return proposedCompsets.slice(0, this.AMOUNT_OF_PROPOSED_COMPSETS);
    }

    return proposedCompsets;
  }

  @action onChangeProposedCompset(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    if (!isPrioritizedCategory(value)) {
      return;
    }

    this.benchmarkInsightCompetitorsCompset.selectedProposedCompsetPrioritizedCategory =
      value;
  }

  @action onClickBack(): void {
    // Use legacyTransitionTo to avoid the parent model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'hotel.settings.competitors.benchmark.compset.builder.desired-compset',
    );
  }

  @action onClickNext(): void {
    // Use legacyTransitionTo to avoid the parent model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'hotel.settings.competitors.benchmark.compset.builder.review',
    );

    this.usageAnalytics.track('Benchmark Compset Reviewed', {
      eventTrigger: 'compset-builder',
    });
  }

  @action onClose(): void {
    // Use legacyTransitionTo to avoid the parent model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'hotel.settings.competitors.benchmark.compset.index',
    );

    this.usageAnalytics.track('Benchmark Compset Cancelled', {
      eventTrigger: 'compset-builder',
    });
  }

  @action selectProposedCompset(
    proposedCompsetPrioritizedCategory: PrioritizedCategory,
  ): void {
    this.benchmarkInsightCompetitorsCompset.selectedProposedCompsetPrioritizedCategory =
      proposedCompsetPrioritizedCategory;

    this.usageAnalytics.track('Benchmark Compset Proposal Selected', {
      eventTrigger: 'compset-builder',
      type: proposedCompsetPrioritizedCategory,
    });
  }
}
